<script setup lang="ts">
import { ICON } from "@/lib/icons"
import type { PrintItem } from "@/types/models"

defineProps<{
  item: PrintItem | null
  text: string
  includeQr: boolean
  quantity: number
  isFull: boolean
  media: string
}>()

const emit = defineEmits<{
  "update:text": [value: string]
  "update:includeQr": [value: boolean]
  "update:quantity": [value: number]
  "clear-item": []
  reset: []
}>()
</script>

<template>
  <div v-if="item" class="border-default bg-muted flex items-center gap-4 rounded-lg border p-4">
    <UIcon
      :name="item.is_container ? ICON.container : ICON.item"
      class="text-muted size-8 shrink-0"
    />
    <div class="min-w-0 flex-1">
      <div class="text-highlighted truncate text-sm font-medium">{{ item.name }}</div>
      <div class="text-muted text-xs">
        {{ item.asset_code ? `Asset code ${item.asset_code}` : "Code assigned when printed" }}
      </div>
    </div>
    <UButton
      :icon="ICON.close"
      aria-label="Choose a different item"
      color="neutral"
      variant="ghost"
      size="md"
      square
      @click="emit('clear-item')"
    />
  </div>

  <p class="text-muted text-sm">
    <span class="text-highlighted font-medium">Selected stock:</span> {{ media }}
  </p>

  <div v-if="isFull" class="flex flex-col gap-4">
    <UFormField label="Label text">
      <UInput
        :model-value="text"
        placeholder="e.g. Kitchen Shelf"
        class="w-full"
        @update:model-value="emit('update:text', $event as string)"
      />
    </UFormField>

    <UCheckbox
      :model-value="includeQr"
      label="Include QR code"
      :description="
        item?.asset_code
          ? `Encodes asset code ${item.asset_code}`
          : item
            ? 'Assigns an asset code when printed'
            : 'Generates an unassigned asset code'
      "
      @update:model-value="emit('update:includeQr', $event as boolean)"
    />
  </div>

  <UFormField label="Copies">
    <UInputNumber
      :model-value="quantity"
      :min="1"
      :max="10"
      :step="1"
      @update:model-value="emit('update:quantity', $event as number)"
    />
  </UFormField>

  <UButton
    v-if="item || text"
    label="Reset"
    :icon="ICON.reset"
    color="neutral"
    variant="ghost"
    block
    @click="emit('reset')"
  />
</template>
